import { el } from './util.js';

/**
 * Local death/match screen. It owns presentation and countdown timing; the UI
 * system remains authoritative over actually respawning or returning home.
 *
 * Public API:
 *   showRespawn(killer, { livesRemaining, delay, autoRespawn, onRespawn })
 *   setRespawnCountdown(seconds, livesRemaining)
 *   showFinal({ scoreUs, scoreThem, result, killer, detail })
 *   setCallbacks({ onRespawn, onReturnHome })
 *
 * `show(killer)` remains the legacy terminal-screen shortcut.
 */
export class DeathScreen {
  constructor(parent, callbacks) {
    this.onReturnHome = typeof callbacks === 'function' ? callbacks : callbacks?.onReturnHome;
    this.onRespawn = typeof callbacks === 'object' ? callbacks?.onRespawn : null;
    this.mode = 'hidden';
    this.livesRemaining = 0;
    this.autoRespawn = true;
    this._deadline = 0;
    this._timer = 0;
    this._respawnRequested = false;
    this._boundTick = () => this._tickCountdown();

    this.root = el('section', 'ow-death-screen', parent);
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-labelledby', 'ow-death-title');

    const panel = el('div', 'ow-death-panel', this.root);
    this.kicker = el('div', 'ow-death-kicker', panel, 'OPERATOR DOWN');
    this.title = el('h1', 'ow-death-title', panel, 'YOU DIED');
    this.title.id = 'ow-death-title';
    this.detail = el('p', 'ow-death-detail', panel, 'YOUR OPERATION HAS ENDED');
    this.detail.setAttribute('aria-live', 'polite');
    this.lives = el('p', 'ow-death-detail', panel, 'RESPAWNS LEFT: 5');
    this.lives.hidden = true;
    this.score = el('p', 'ow-death-detail', panel, '0 — 0');
    this.score.hidden = true;

    this.respawnButton = el('button', 'ow-death-return', panel, 'RESPAWNING IN 3');
    this.respawnButton.type = 'button';
    this.respawnButton.hidden = true;
    this.respawnButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!this.respawnButton.disabled) this._requestRespawn();
    });

    this.returnButton = el('button', 'ow-death-return', panel, 'Return to Home');
    this.returnButton.type = 'button';
    this.returnButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.onReturnHome?.();
    });
  }

  setCallbacks(callbacks = {}) {
    if (callbacks.onRespawn !== undefined) this.onRespawn = callbacks.onRespawn;
    if (callbacks.onReturnHome !== undefined) this.onReturnHome = callbacks.onReturnHome;
  }

  /** Backwards-compatible terminal state used by the existing UI integration. */
  show(killer, options) {
    if (options?.mode === 'respawn' || options?.final === false) {
      this.showRespawn(killer, options);
      return;
    }
    this.showFinal({ ...(options ?? {}), killer });
  }

  showRespawn(killer, options = {}) {
    this._clearTimer();
    this.mode = 'respawn';
    this.root.dataset.mode = 'respawn';
    this._respawnRequested = false;
    this.livesRemaining = Math.max(0, options.livesRemaining ?? options.lives ?? 0) | 0;
    this.autoRespawn = options.autoRespawn !== false;
    if (typeof options.onRespawn === 'function') this.onRespawn = options.onRespawn;

    this.kicker.textContent = 'OPERATOR DOWN';
    this.title.textContent = 'YOU DIED';
    this.detail.textContent = killer
      ? `ELIMINATED BY ${String(killer).toUpperCase()}`
      : 'REDEPLOYMENT AVAILABLE';
    this.lives.textContent = `RESPAWNS LEFT: ${this.livesRemaining}`;
    this.lives.hidden = false;
    this.score.hidden = true;
    this.returnButton.hidden = true;
    this.respawnButton.hidden = false;
    this.respawnButton.disabled = true;

    const delay = Math.max(0, Number.isFinite(options.delay) ? options.delay : 3);
    const now = globalThis.performance?.now?.() ?? Date.now();
    this._deadline = now + delay * 1000;
    this.root.hidden = false;
    this.setRespawnCountdown(delay, this.livesRemaining);
    if (delay > 0) this._timer = globalThis.setTimeout(this._boundTick, 100);
  }

  /** Allows an engine-owned deterministic timer to drive the same view. */
  setRespawnCountdown(seconds, livesRemaining = this.livesRemaining) {
    this.livesRemaining = Math.max(0, livesRemaining) | 0;
    this.lives.textContent = `RESPAWNS LEFT: ${this.livesRemaining}`;
    const remaining = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    if (remaining > 0) {
      this.respawnButton.disabled = true;
      this.respawnButton.textContent = `RESPAWNING IN ${Math.max(1, Math.ceil(remaining))}`;
      return;
    }
    this.respawnButton.disabled = false;
    this.respawnButton.textContent = 'RESPAWN';
    if (this.autoRespawn) this._requestRespawn();
    else this.respawnButton.focus({ preventScroll: true });
  }

  showFinal(options = {}) {
    this._clearTimer();
    this.mode = 'final';
    this.root.dataset.mode = 'final';
    const us = Number.isFinite(options.scoreUs) ? options.scoreUs : null;
    const them = Number.isFinite(options.scoreThem) ? options.scoreThem : null;
    const result = options.result
      ?? (us !== null && them !== null ? (us > them ? 'VICTORY' : us < them ? 'DEFEAT' : 'DRAW') : 'MATCH OVER');
    this.kicker.textContent = 'FINAL SCORE';
    this.title.textContent = String(result).toUpperCase();
    this.detail.textContent = options.detail
      ?? (options.killer ? `ELIMINATED BY ${String(options.killer).toUpperCase()}` : 'YOUR OPERATION HAS ENDED');
    if (us !== null && them !== null) {
      this.score.textContent = `${us} — ${them}`;
      this.score.hidden = false;
    } else {
      this.score.hidden = true;
    }
    this.lives.hidden = true;
    this.respawnButton.hidden = true;
    this.returnButton.hidden = false;
    this.root.hidden = false;
    this.returnButton.focus({ preventScroll: true });
  }

  _tickCountdown() {
    this._timer = 0;
    if (this.mode !== 'respawn' || this.root.hidden) return;
    const now = globalThis.performance?.now?.() ?? Date.now();
    const remaining = Math.max(0, (this._deadline - now) / 1000);
    this.setRespawnCountdown(remaining, this.livesRemaining);
    if (remaining > 0 && this.mode === 'respawn') {
      this._timer = globalThis.setTimeout(this._boundTick, 100);
    }
  }

  _requestRespawn() {
    if (this._respawnRequested || this.mode !== 'respawn') return;
    this._respawnRequested = true;
    this._clearTimer();
    const callback = this.onRespawn;
    this.hide();
    callback?.({ livesRemaining: this.livesRemaining });
  }

  _clearTimer() {
    if (this._timer) globalThis.clearTimeout(this._timer);
    this._timer = 0;
  }

  hide() {
    this._clearTimer();
    this.mode = 'hidden';
    delete this.root.dataset.mode;
    this.root.hidden = true;
  }

  dispose() {
    this._clearTimer();
    this.root.remove();
  }
}
